import { useState } from "react";
import { createListStore, getFilterDto, getSort } from "../../common/listStore";
import { canResendOrderConfirmation, TmOrderListItemDto } from "./common";
import { PageTitle } from "../../layout/PageTitle";
import { listHeaderItems, renderListStore } from "../../common/listStoreUi";
import { Button, Grid } from "@mui/material";
import { Add, Preview } from "@mui/icons-material";
import { useRxJsSub } from "../../common/utils";
import { interval } from "rxjs";
import { getResource, useLocalizedPropValidators, useResources } from "../../common/localization";
import { useDialog } from "../../common/dialog";
import { OrderConfirmationDialog } from "./OrderConfirmationDialog";
import { useDefaultValues } from "../../common/defaultValues";
import { useAccountStore, UserTypeId } from "../../common/account";
import { EnumItemDto, OrderStateId, useCallCenters, useOrderStatesLocalized, useYesNo } from "../../common/enum";
import { createEntityStore } from "../../common/entityStore";
import { propValidValue } from "../../common/entityStoreValidators";
import { applyListStoreFilters, nextDay } from "../../common/listStoreFilters";
import { propDateField, propEnumField, propNumberField, propTextField } from "../../common/entityStoreForm";
import { downloadFromApiPost } from "../../common/download";

function getText(key: string, defaultMessage?: string) {
    return getResource("Ui.Order." + key, defaultMessage) as string;
}

function getFiltersStoreInitialData() {
    return {};
}

interface ListFilters {
    callCenterId?: number;
    from?: string;
    to?: string;
    fromFormatted?: string;
    toFormatted?: string;
    ordId?: number;
    ordIdFormatted?: string;
    userId?: number;
    custId?: string;
    phone?: string;
    confirmedByCustomer?: string;
    customerName?: string;
    description?: string;
    orderStateId?: number;
}

export function TmOrderListPage() {
    const resources = useResources();
    return resources.ready ? <TmOrderListPageInternal /> : <></>;
}

function TmOrderListPageInternal() {
    const [useUsers] = useState(() => createListStore<EnumItemDto>({ path: "tm/users" }));
    const { isTmOrderConfirmationRequired } = useDefaultValues(p => ({ isTmOrderConfirmationRequired: p.clientData.isTmOrderConfirmationRequired }));
    const userTypeId = useAccountStore(p => p.data?.accessTokenDecoded.userTypeId);
    const isAdmin = userTypeId === UserTypeId.Admin;
    const isAdminOrTmSupervisor = isAdmin || userTypeId === UserTypeId.TmSupervisor || userTypeId === UserTypeId.Supervisor || userTypeId === UserTypeId.TmManager;
    const [{ useStore, useFiltersStore }] = useState(() => {
        const useStore = createListStore<TmOrderListItemDto>({
            path: "tm/orders/list",
            itemsPerPage: 15,
            supportsApiFilterSortPage: true,
            listProps: [
                isAdmin && { column: "callCenterName", label: getResource("Ui.CallCenter") as string, width: 150 },
                isAdminOrTmSupervisor && { column: "userName", label: getResource("Ui.UserName") as string, width: 150 },
                { column: "custId", label: getText("CustId"), width: 90, align: "right" },
                { column: "tel", label: getResource("Ui.Customer.Tel") as string, width: 90 },
                { column: "mob", label: getResource("Ui.Customer.Mob") as string, width: 90 },
                { column: "ordId", label: getText("OrdId"), width: 70, type: "number" },
                { column: "orderStateId", label: getText("OrderState"), width: 100, enumStore: useOrderStatesLocalized },
                { column: "ordDte", label: getText("Date"), width: 160, type: "datetime" },
                { column: "confirmedByCustomer", label: getText("ConfirmedByCustomer"), width: 100, type: "bool" },
                { column: "comgatePaymentState", label: getText("ComgatePaymentState") as string, width: 100 },
                { column: "affidavit", label: getText("Affidavit"), width: 100, type: "bool" },
                { column: "customerName", label: getText("CustomerName"), width: 250 },
                { column: "description", label: getText("Description"), width: isAdminOrTmSupervisor ? 400 : undefined, flex: isAdminOrTmSupervisor ? undefined : 1 },
                isAdminOrTmSupervisor && { column: "priceTotal", label: getText("PriceTotal"), width: 100, type: "currency" },
                isAdminOrTmSupervisor && { column: "priceWithoutPostage", label: getText("PriceWithoutPostage"), width: 100, type: "currency" },
                isAdminOrTmSupervisor && { column: "articles", label: getText("Articles"), flex: 1 },
            ],
            getId: p => p.id,
        });
        const useFiltersStore = createEntityStore<ListFilters>({
            data: getFiltersStoreInitialData(), isValidationEnabled: true,
            propValidators: [
                propValidValue("from", "fromFormatted"),
                propValidValue("to", "toFormatted"),
                propValidValue("ordId", "ordIdFormatted"),
            ]
        });
        applyListStoreFilters(useFiltersStore, useStore, [
            { listProps: ["callCenterId"], operator: "Equal", filterProp: "callCenterId" },
            { listProps: ["userId"], operator: "Equal", filterProp: "userId" },
            { listProps: ["ordDte"], operator: "GreaterThanOrEqual", filterProp: "from" },
            { listProps: ["ordDte"], operator: "LessThan", filterProp: "to", transform: nextDay },
            { listProps: ["orderStateId"], operator: "Equal", filterProp: "orderStateId" },
            { listProps: ["confirmedByCustomer"], operator: "Equal", filterProp: "confirmedByCustomer", transform: p => p === "true" ? true : p === "false" ? false : undefined },
            { listProps: ["custId"], operator: "Like", filterProp: "custId", fullText: true },
            { listProps: ["tel", "mob"], operator: "Like", filterProp: "phone" },
            { listProps: ["customerName"], operator: "Like", filterProp: "customerName", fullText: true },
            { listProps: ["description"], operator: "Like", filterProp: "description", fullText: true },
            { listProps: ["ordId"], operator: "Equal", filterProp: "ordId" },
        ]);
        return { useStore, useFiltersStore };
    });
    useLocalizedPropValidators(useFiltersStore);
    useRxJsSub(() => interval(10_000).subscribe(() => useStore.getState().fetch({ unintrusive: true })));
    const orderConfirmationDialog = useDialog(close => <OrderConfirmationDialog close={close} orderId={selectedOrder!.id} />);
    const selectedOrder = useStore(p => p.selectedItem);
    function viewOrder(order: TmOrderListItemDto) {
        window.open(`/tm/orders/${order.custId}-${order.ordId}`);
    }
    function clearFiltersStore() {
        useFiltersStore.getState().setClientData(getFiltersStoreInitialData());
    }

    return <>
        {orderConfirmationDialog.render()}
        <PageTitle title={getResource("Ui.TmOrders") as string} />
        {renderListStore(useStore, {
            header: listHeaderItems([
                ["add", () => <Button color="add" title={getResource("Ui.Add") as string} onClick={() => window.open("/tm/orders/new")}><Add /></Button>],
                ["view", () => <Button color="edit" title={getResource("Ui.View") as string} disabled={selectedOrder == null} onClick={() => viewOrder(selectedOrder!)}><Preview /></Button>],
                isTmOrderConfirmationRequired && ["resendConfirmationRequest", () => <Button color="edit" disabled={
                    selectedOrder == null ||
                    selectedOrder.confirmedByCustomer ||
                    selectedOrder.isCustomerDeleted ||
                    !canResendOrderConfirmation(selectedOrder.orderStateId)}
                    onClick={() => orderConfirmationDialog.show()}>{getText("ResendConfirmationRequest")}</Button>],
                isAdminOrTmSupervisor && ["export", () => <Button onClick={() => downloadFromApiPost(`tm/orders/list/csv`, {
                    filter: getFilterDto(useStore.getState()),
                    sort: getSort(useStore.getState()),
                })}>{getResource("Ui.Export", "Export")}</Button>],
            ]),
            onDoubleClick(item) {
                viewOrder(item);
            },
            renderExternalFilters: () => <>
                {isAdmin && <Grid item width={200}>{propEnumField(useFiltersStore)("callCenterId", getResource("Ui.CallCenter") as string, useCallCenters, { addAll: true, autoComplete: true })}</Grid>}
                {isAdminOrTmSupervisor && <Grid item width={200}>{propEnumField(useFiltersStore)("userId", getResource("Ui.UserName") as string, useUsers, { addAll: true, autoComplete: true })}</Grid>}
                <Grid item width={150}>{propTextField(useFiltersStore)("custId", getText("CustId"))}</Grid>
                <Grid item width={150}>{propTextField(useFiltersStore)("phone", getResource("Ui.Phone") as string)}</Grid>
                <Grid item width={150}>{propNumberField(useFiltersStore)("ordId", "ordIdFormatted", getText("OrdId"))}</Grid>
                <Grid item width={150}>{propEnumField(useFiltersStore)("orderStateId", getText("OrderState"), useOrderStatesLocalized, {
                    addAll: true,
                    filter: p => [OrderStateId.Active, OrderStateId.Passive, OrderStateId.Shipped].includes(p.id as OrderStateId),
                })}</Grid>
                <Grid item width={160}>{propDateField(useFiltersStore)("from", "fromFormatted", getResource("Ui.DateFrom") as string)}</Grid>
                <Grid item width={160}>{propDateField(useFiltersStore)("to", "toFormatted", getResource("Ui.DateTo") as string)}</Grid>
                <Grid item width={150}>{propEnumField(useFiltersStore)("confirmedByCustomer", getText("ConfirmedByCustomer"), useYesNo, { addAll: true })}</Grid>
                <Grid item width={200}>{propTextField(useFiltersStore)("customerName", getText("CustomerName"))}</Grid>
                <Grid item width={200}>{propTextField(useFiltersStore)("description", getText("Description"))}</Grid>
            </>, clearExternalFilters: clearFiltersStore,
            onClear: () => useStore.getState().fetch({ updateFetchFilterData: true }),
            getRowClass: p => (p.isCustomerDeleted && ["redRibbon", "strikeThrough"]),
        })}
    </>;
}
